import React, { useState, useRef, useEffect } from "react";
import { streamSSE, downloadExport, api } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { DRAFT } from "@/constants/testIds";
import { toast } from "sonner";
import {
  FileSignature,
  FileText,
  ShieldCheck,
  BookOpen,
  Cpu,
  ClipboardList,
  Lock,
  Stethoscope,
  ArrowLeft,
  Download,
  Eye,
  Sparkles,
  Loader2,
  AlertCircle,
  PlayCircle,
  CheckCircle2,
  GitBranchPlus,
  Globe2,
  Gavel,
} from "lucide-react";
import ReviewerSimulator from "@/components/ReviewerSimulator";

const DOC_TYPES = [
  { key: "510k",           title: "510(k) Notification",   meta: "21 CFR 807",        icon: FileSignature, mode: "FDA", blurb: "Substantial equivalence submission for Class II devices." },
  { key: "pma",            title: "PMA Application",       meta: "21 CFR 814",        icon: FileText,       mode: "FDA", blurb: "Premarket approval outline for Class III devices." },
  { key: "de_novo",        title: "De Novo Request",       meta: "21 CFR 860.200",    icon: BookOpen,       mode: "FDA", blurb: "Novel classification pathway for low-to-moderate risk devices." },
  { key: "ifu",            title: "Instructions for Use",  meta: "21 CFR 801",        icon: Stethoscope,    mode: "FDA", blurb: "Patient/clinician-facing IFU per FDA labeling rules." },
  { key: "dhf",            title: "Design History File",   meta: "820.30(j)",         icon: ClipboardList,  mode: "FDA", blurb: "Index & templates for a complete DHF." },
  { key: "risk_mgmt",      title: "Risk Management File",  meta: "ISO 14971:2019",    icon: ShieldCheck,    mode: "FDA", blurb: "Hazards, risk analysis, controls & residual risk." },
  { key: "design_controls",title: "Design Controls SOP",   meta: "21 CFR 820.30",     icon: Cpu,            mode: "FDA", blurb: "Design & development SOP procedure." },
  { key: "cyber",          title: "Cybersecurity Docs",    meta: "FDA 2023 Guidance", icon: Lock,           mode: "FDA", blurb: "Premarket cybersecurity documentation package." },
  { key: "ce_tech_doc",    title: "EU MDR Technical Doc",  meta: "EU MDR Annex II",   icon: Globe2,         mode: "EU",  blurb: "Full Annex II technical documentation dossier for CE marking." },
  { key: "ce_cer",         title: "Clinical Evaluation Report", meta: "MDR Annex XIV", icon: BookOpen,      mode: "EU",  blurb: "CER per MEDDEV 2.7/1 rev 4 and MDR Annex XIV." },
  { key: "ce_pms",         title: "Post-Market Surveillance Plan", meta: "MDR Annex III", icon: ShieldCheck, mode: "EU", blurb: "PMS Plan per MDR Article 84 with PMCF linkage." },
  { key: "change_control", title: "Change-Control Decision", meta: "FDA 2017 Guidance", icon: GitBranchPlus, mode: "FDA", blurb: "Letter to File vs. Special 510(k) decision walk-through." },
  // ----- Drug templates (CDER) -----
  { key: "ind",            title: "IND Application",        meta: "21 CFR 312",        icon: FileText,       mode: "DRUG", blurb: "Full Investigational New Drug application per 21 CFR 312." },
  { key: "nda",            title: "NDA (New Drug App)",     meta: "21 CFR 314 · eCTD", icon: FileSignature,  mode: "DRUG", blurb: "eCTD Modules 1-5 template for a New Drug Application." },
  { key: "anda",           title: "ANDA (Generic)",         meta: "21 CFR 314.94",     icon: BookOpen,       mode: "DRUG", blurb: "Generic-drug application with RLD basis and BE study framework." },
  { key: "drug_label",     title: "Drug Label / PI",        meta: "21 CFR 201.57",     icon: ClipboardList,  mode: "DRUG", blurb: "Full FDA Prescribing Information per the Physician Labeling Rule." },
  { key: "cmc",            title: "CMC (Module 3 Quality)", meta: "ICH M4Q",           icon: Cpu,            mode: "DRUG", blurb: "Chemistry, Manufacturing, and Controls per ICH M4Q." },
  { key: "otc_monograph",  title: "OTC Monograph Compliance", meta: "21 CFR 330",      icon: ShieldCheck,    mode: "DRUG", blurb: "OTC drug monograph + Drug Facts label compliance memo." },
];

const EMPTY = {
  device_name: "",
  intended_use: "",
  device_class: "II",
  predicate_device: "",
  key_features: "",
  target_population: "",
  additional_notes: "",
  rld_application_numbers: "",
};

const DRUG_DOC_TYPES = new Set(["ind", "nda", "anda", "drug_label", "cmc", "otc_monograph"]);

/**
 * DraftingPanel
 *   props:
 *     preset – { project_id?, device_name? } to prefill from a project
 *     onDraftCreated – callback fired when a draft is saved (used by parent to refresh Library/Project)
 */
export default function DraftingPanel({ preset, onDraftCreated }) {
  const [selected, setSelected] = useState(null);
  const [modeFilter, setModeFilter] = useState("ALL");   // 'FDA' | 'EU' | 'ALL'
  const [form, setForm] = useState({ ...EMPTY, device_name: preset?.device_name || "" });
  const [projectId, setProjectId] = useState(preset?.project_id || "");
  const [projects, setProjects] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [docId, setDocId] = useState(null);
  const [status, setStatus] = useState(null); // 'streaming' | 'complete' | 'truncated' | 'error'
  const [gotDone, setGotDone] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [reviewerOpen, setReviewerOpen] = useState(false);
  const outRef = useRef(null);

  const selectedMeta = DOC_TYPES.find((d) => d.key === selected);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (preset?.project_id) setProjectId(preset.project_id);
    if (preset?.device_name) setForm((f) => ({ ...f, device_name: preset.device_name }));
  }, [preset]);

  const generate = async () => {
    if (!form.device_name.trim() || !form.intended_use.trim()) {
      toast.error("Device name and intended use are required.");
      return;
    }
    setContent(""); setDocId(null); setStatus("streaming"); setGotDone(false);
    setStreaming(true);
    try {
      let buf = "";
      let doneFrame = false;
      let newDocId = null;
      // Parse RLD app numbers for drug docs
      const rldRaw = (form.rld_application_numbers || "").trim();
      const rldList = rldRaw
        ? rldRaw.split(/[,\s]+/).map((s) => s.trim().toUpperCase()).filter(Boolean)
        : null;
      await streamSSE(
        "/documents/draft/stream",
        {
          doc_type: selected,
          ...form,
          project_id: projectId || null,
          rld_application_numbers: rldList,
        },
        (frame) => {
          if (frame.doc_id) { setDocId(frame.doc_id); newDocId = frame.doc_id; }
          if (frame.title) setTitle(frame.title);
          if (frame.delta) {
            buf += frame.delta;
            setContent(buf);
            if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
          }
          if (frame.done) { doneFrame = true; setStatus(frame.status || "complete"); setGotDone(true); }
          if (frame.error) { toast.error(frame.error); setStatus("error"); }
        }
      );
      if (!doneFrame) {
        // Stream ended without an explicit 'done' frame — likely ingress cut-off.
        // The server-side try/finally has already persisted whatever we got as 'truncated'.
        setStatus("truncated");
        toast.info("Draft was cut off. Content is saved · click Continue to resume.");
      } else {
        toast.success("Draft complete");
      }
      onDraftCreated?.(newDocId);
    } catch (e) {
      setStatus("truncated");
      toast.info("Connection dropped. Your partial draft is saved · click Continue to resume.");
    } finally {
      setStreaming(false);
    }
  };

  const continueDraft = async () => {
    if (!docId) return;
    setResuming(true); setStatus("streaming"); setGotDone(false);
    try {
      let buf = content;
      let doneFrame = false;
      await streamSSE(
        `/documents/${docId}/continue`,
        {},
        (frame) => {
          if (frame.delta) {
            buf += frame.delta;
            setContent(buf);
            if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
          }
          if (frame.done) { doneFrame = true; setStatus(frame.status || "complete"); setGotDone(true); }
          if (frame.error) { toast.error(frame.error); setStatus("error"); }
        }
      );
      if (!doneFrame) {
        setStatus("truncated");
        toast.info("Still more to write. Click Continue again.");
      } else {
        toast.success("Draft resumed and completed");
      }
    } catch (e) {
      setStatus("truncated");
      toast.info("Connection dropped again. Content saved · click Continue to resume.");
    } finally {
      setResuming(false);
    }
  };

  const [critique, setCritique] = useState("");
  const [critiquing, setCritiquing] = useState(false);
  const [showCritique, setShowCritique] = useState(false);

  const runCritique = async () => {
    if (!content || content.length < 200) return toast.error("Draft too short to critique");
    setCritiquing(true); setCritique(""); setShowCritique(true);
    try {
      let buf = "";
      await streamSSE("/documents/critique", {
        draft_content: content, doc_type: selected, device_name: form.device_name,
      }, (frame) => {
        if (frame.delta) { buf += frame.delta; setCritique(buf); }
        if (frame.error) toast.error(frame.error);
      });
      toast.success("Reviewer critique ready");
    } catch (e) {
      toast.info("Critique stream ended");
    } finally { setCritiquing(false); }
  };

  const doExport = async (fmt) => {
    if (!content) return;
    setExporting(true);
    try {
      await downloadExport(title || `${selectedMeta?.title || "Document"} · ${form.device_name}`, content, fmt);
      toast.success(`Exported as ${fmt.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const reset = () => {
    setSelected(null); setForm({ ...EMPTY, device_name: preset?.device_name || "" });
    setContent(""); setTitle(""); setDocId(null); setStatus(null); setGotDone(false);
  };

  // ---------- Gallery ----------
  if (!selected) {
    return (
      <div>
        <div className="mb-10">
          <div className="overline mb-3 text-slate-500">STEP 1 / 03</div>
          <h1 className="font-serif text-4xl font-semibold tracking-tight lg:text-5xl">
            Choose a document to draft.
          </h1>
          <p className="mt-3 max-w-xl text-slate-500">
            Pick a template. Each one is structured per its exact FDA / ISO requirements.
          </p>
          {preset?.project_id && (
            <div className="mt-4 inline-flex items-center gap-2 border border-[#2563EB]/40 bg-[#2563EB]/10 px-3 py-1.5">
              <span className="overline text-[#2563EB]">SAVING TO PROJECT</span>
              <span className="text-sm text-slate-800">{projects.find(p => p.id === preset.project_id)?.name || preset.project_id}</span>
            </div>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {["ALL","FDA","EU","DRUG"].map((m) => (
              <button
                key={m}
                data-testid={`draft-mode-${m.toLowerCase()}`}
                onClick={() => setModeFilter(m)}
                className={`border px-3 py-1.5 text-xs tracking-wide transition-colors ${
                  modeFilter === m
                    ? m === "DRUG"
                      ? "border-purple-400 bg-purple-500/15 text-white"
                      : "border-[#2563EB] bg-[#2563EB]/15 text-white"
                    : "border-slate-200 bg-[#FAFAF7] text-slate-500 hover:border-slate-300 hover:text-slate-900"
                }`}
              >
                {m === "ALL" ? "All" : m === "FDA" ? "US FDA (Devices)" : m === "EU" ? "EU MDR / CE" : "Drugs (CDER)"}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-px bg-slate-100 sm:grid-cols-2 xl:grid-cols-3">
          {DOC_TYPES.filter((d) => modeFilter === "ALL" || d.mode === modeFilter).map((d) => {
            const Icon = d.icon;
            return (
              <button
                key={d.key}
                data-testid={DRAFT.docTypeCard}
                onClick={() => setSelected(d.key)}
                className="group flex flex-col items-start bg-[#FAFAF7] p-6 text-left transition-all hover:bg-slate-50"
              >
                <div className="mb-8 flex w-full items-start justify-between">
                  <Icon className="h-7 w-7 text-[#2563EB] transition-transform group-hover:scale-110" strokeWidth={1.5} />
                  <span className="font-mono text-[10px] text-slate-400">{d.meta}</span>
                </div>
                <div className="overline mb-2 text-slate-500">TEMPLATE</div>
                <div className="font-serif text-xl font-semibold leading-tight text-slate-900">{d.title}</div>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">{d.blurb}</p>
                <span className="mt-6 overline text-[#2563EB] opacity-0 transition-opacity group-hover:opacity-100">
                  Draft this →
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ---------- Form + Output ----------
  return (
    <div>
      <button
        onClick={reset}
        data-testid={DRAFT.backBtn}
        className="mb-6 inline-flex items-center gap-2 overline text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-3 w-3" /> All templates
      </button>

      <div className="grid gap-8 lg:grid-cols-[420px_1fr]">
        {/* FORM */}
        <div className="border border-slate-200 bg-white p-6">
          <div className="mb-6">
            <div className="overline mb-1 text-[#2563EB]">{selectedMeta.meta}</div>
            <h2 className="font-serif text-2xl font-semibold leading-tight">{selectedMeta.title}</h2>
            <p className="mt-2 text-sm text-slate-500">{selectedMeta.blurb}</p>
          </div>

          <div className="space-y-4">
            {projects.length > 0 && (
              <div>
                <label className="overline mb-2 block text-slate-500">Save to project (optional)</label>
                <select
                  data-testid="draft-project-select"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 focus:border-[#2563EB] focus:outline-none"
                >
                  <option value="">— Unassigned (Library only) —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}

            <Field label="Device name *" testid={DRAFT.deviceNameInput}
              value={form.device_name}
              onChange={(v) => setForm({ ...form, device_name: v })}
              placeholder="e.g., CardioSense Pulse Oximeter" />
            <Field label="Intended use *" testid={DRAFT.intendedUseInput} multiline
              value={form.intended_use}
              onChange={(v) => setForm({ ...form, intended_use: v })}
              placeholder="Continuous SpO2 monitoring in adult hospital patients." />

            <div>
              <label className="overline mb-2 block text-slate-500">Device class</label>
              <select
                data-testid={DRAFT.deviceClassSelect}
                value={form.device_class}
                onChange={(e) => setForm({ ...form, device_class: e.target.value })}
                className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 focus:border-[#2563EB] focus:outline-none"
              >
                <option value="I">Class I</option>
                <option value="II">Class II</option>
                <option value="III">Class III</option>
              </select>
            </div>

            <Field label="Predicate device (K number, name)" testid={DRAFT.predicateInput}
              value={form.predicate_device}
              onChange={(v) => setForm({ ...form, predicate_device: v })}
              placeholder="K221234 · Acme Model X" />
            <Field label="Key features / technology" testid={DRAFT.featuresInput} multiline
              value={form.key_features}
              onChange={(v) => setForm({ ...form, key_features: v })}
              placeholder="Photoplethysmography sensor, BLE, iOS/Android app" />
            <Field label="Target population" testid={DRAFT.populationInput}
              value={form.target_population}
              onChange={(v) => setForm({ ...form, target_population: v })}
              placeholder="Adults ≥ 18 in acute-care setting" />
            <Field label="Additional notes" testid={DRAFT.notesInput} multiline
              value={form.additional_notes}
              onChange={(v) => setForm({ ...form, additional_notes: v })}
              placeholder="Any regulatory context, prior interactions, special controls…" />

            {DRUG_DOC_TYPES.has(selected) && (
              <div>
                <label className="overline mb-2 block text-purple-300">
                  RLD Application Numbers <span className="text-slate-400">— anchor to real drugs</span>
                </label>
                <input
                  data-testid="draft-rld-input"
                  value={form.rld_application_numbers}
                  onChange={(e) => setForm({ ...form, rld_application_numbers: e.target.value })}
                  placeholder="NDA019537, ANDA206392 (comma or space separated)"
                  className="w-full border border-purple-400/30 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-300 focus:border-purple-400 focus:outline-none"
                />
                <div className="mt-1 text-[11px] text-slate-400">
                  Qualmitta pulls live label + drugsfda data and grounds every RLD comparison, safety and route claim in it.
                </div>
              </div>
            )}

            <button
              data-testid={DRAFT.generateBtn}
              disabled={streaming || resuming}
              onClick={generate}
              className="mt-2 inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-5 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-50"
            >
              {streaming ? <><Loader2 className="h-4 w-4 animate-spin" /> Drafting…</> : <><Sparkles className="h-4 w-4" /> Generate draft</>}
            </button>
          </div>
        </div>

        {/* OUTPUT */}
        <div className="border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="overline text-[#2563EB]">AI DRAFT</div>
                <StatusPill status={status} />
              </div>
              <div className="truncate font-serif text-lg font-semibold">
                {title || "Your draft will appear here"}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                data-testid="draft-critique-btn"
                disabled={!content || critiquing || content.length < 200}
                onClick={runCritique}
                title="Reviewer-eye critique (as if an FDA lead reviewer read it)"
                className="inline-flex items-center gap-1.5 border border-[#2563EB]/50 bg-[#2563EB]/10 px-3 py-2 text-xs font-medium tracking-wide text-[#93C5FD] transition-all hover:border-[#2563EB] hover:bg-[#2563EB]/20 disabled:opacity-40"
              >
                {critiquing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                Critique
              </button>
              <button
                data-testid="draft-simulate-reviewer-btn"
                disabled={!content || content.length < 200}
                onClick={() => setReviewerOpen(true)}
                title="Reviewer Simulator — role-plays an FDA reviewer producing section-by-section deficiencies"
                className="inline-flex items-center gap-1.5 border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-xs font-medium tracking-wide text-amber-200 transition-all hover:border-amber-300 hover:bg-amber-500/20 disabled:opacity-40"
              >
                <Gavel className="h-3.5 w-3.5" />
                Reviewer sim
              </button>
              <button
                data-testid={DRAFT.exportPdfBtn}
                disabled={!content || exporting}
                onClick={() => doExport("pdf")}
                className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-2 text-xs font-medium tracking-wide text-slate-700 transition-all hover:border-[#2563EB] hover:text-slate-900 disabled:opacity-40"
              >
                <Download className="h-3.5 w-3.5" /> PDF
              </button>
              <button
                data-testid={DRAFT.exportDocxBtn}
                disabled={!content || exporting}
                onClick={() => doExport("docx")}
                className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-2 text-xs font-medium tracking-wide text-slate-700 transition-all hover:border-[#2563EB] hover:text-slate-900 disabled:opacity-40"
              >
                <Download className="h-3.5 w-3.5" /> DOCX
              </button>
            </div>
          </div>

          {status === "truncated" && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-500/25 bg-amber-500/8 px-6 py-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <div className="text-sm text-amber-100/90">
                  <b>Draft was cut off.</b> Your content is saved. Click Continue to resume exactly where it stopped.
                </div>
              </div>
              <button
                data-testid="draft-continue-btn"
                onClick={continueDraft}
                disabled={resuming}
                className="inline-flex items-center gap-2 border border-amber-400/60 bg-amber-500/15 px-3 py-2 text-xs font-semibold text-amber-100 hover:bg-amber-500/25 disabled:opacity-60"
              >
                {resuming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
                Continue draft
              </button>
            </div>
          )}

          {status === "complete" && gotDone && content && (
            <div className="flex items-center gap-2 border-b border-emerald-500/25 bg-emerald-500/8 px-6 py-2.5 text-xs text-emerald-200/90">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
              Draft complete · saved to your library.
            </div>
          )}

          <div
            ref={outRef}
            data-testid={DRAFT.output}
            className="max-h-[75vh] overflow-y-auto p-6 lg:p-10"
          >
            {!content && !streaming && (
              <div className="flex h-64 flex-col items-center justify-center text-center text-slate-400">
                <FileSignature className="mb-4 h-8 w-8" strokeWidth={1.5} />
                <div className="overline">FILL THE FORM AND HIT GENERATE</div>
              </div>
            )}
            {(streaming || resuming) && !content && (
              <div className="flex items-center gap-2 text-slate-500">
                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                <span className="ml-2 text-sm">Claude is drafting…</span>
              </div>
            )}
            {content && <Markdown>{content}</Markdown>}
            {(streaming || resuming) && content && (
              <div className="mt-2 inline-flex items-center gap-2 text-xs text-slate-400">
                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
              </div>
            )}
          </div>
          {showCritique && (
            <div data-testid="draft-critique-panel" className="border-t border-[#2563EB]/30 bg-white/60 p-6 lg:p-10">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-[#93C5FD]" />
                  <div className="overline text-[#93C5FD]">REVIEWER-EYE CRITIQUE</div>
                </div>
                <button
                  onClick={() => setShowCritique(false)}
                  className="text-slate-400 hover:text-slate-700 text-xs"
                >
                  Hide
                </button>
              </div>
              {critique ? (
                <Markdown>{critique}</Markdown>
              ) : critiquing ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                  <span className="ml-2">Reading as an FDA reviewer…</span>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
      <ReviewerSimulator
        open={reviewerOpen}
        onClose={() => setReviewerOpen(false)}
        docType={selected}
        productType={DRUG_DOC_TYPES.has(selected) ? "drug" : "device"}
        deviceName={form.device_name}
        intendedUse={form.intended_use}
        content={content}
      />
    </div>
  );
}

function StatusPill({ status }) {
  if (!status) return null;
  const map = {
    streaming: { c: "border-blue-400/50 text-blue-300 bg-blue-500/10",  l: "STREAMING" },
    complete:  { c: "border-emerald-400/50 text-emerald-300 bg-emerald-500/10", l: "COMPLETE" },
    truncated: { c: "border-amber-400/50 text-amber-300 bg-amber-500/10", l: "TRUNCATED" },
    error:     { c: "border-red-400/50 text-red-300 bg-red-500/10", l: "ERROR" },
  };
  const s = map[status] || null;
  if (!s) return null;
  return <span className={`border px-1.5 py-0.5 font-mono text-[9px] tracking-wider ${s.c}`}>{s.l}</span>;
}

function Field({ label, value, onChange, placeholder, multiline, testid }) {
  return (
    <div>
      <label className="overline mb-2 block text-slate-500">{label}</label>
      {multiline ? (
        <textarea
          data-testid={testid}
          value={value}
          rows={3}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full resize-none border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
        />
      ) : (
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
        />
      )}
    </div>
  );
}
